import { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import AvatarUser from '../../components/AvatarUser';
import { useClientsContext } from './Context';
import { useParamsContext } from '../../contexts/ParamsContext';
import { FilterClient } from './FilterClient';
import { ViewClient } from './view/View';
import { AtendentesService } from '../atendentes/Service';
import { normalizeSearch } from '../recorrencias/helpers';
import { DataRow, formatPhone, getSelfSchedulingLabel, onlyDigits } from './helpers';
import './Clientes.css';
import { Ban, ChevronRight, Contact, Search, TriangleAlert, UserX } from 'lucide-react';

const atendentesService = new AtendentesService();
const PAGE_SIZE = 60;

// Acima desta largura, ja descontado o menu lateral, lista e detalhe aberto cabem lado a lado.
export const CLIENTES_TWO_PANE_MIN_WIDTH = 1180;

// Ordem e secao usam o mesmo texto: um espaco no inicio do nome nao pode separar o cliente da sua letra.
const getSortName = (row: DataRow) => normalizeSearch(row.displayName || row.email);

const getInitial = (row: DataRow) => {
    const initial = getSortName(row).charAt(0).toUpperCase();
    return /[A-Z]/.test(initial) ? initial : '#';
};

export default function ClientsList() {
    const { data, loading, error, loadClients, filters, setFilters } = useClientsContext();
    const { search, grupoId, atendenteId, selfSchedulingStatus } = filters;
    const { id, setParams, clear } = useParamsContext();
    const isTwoPane = useMediaQuery({ minWidth: CLIENTES_TWO_PANE_MIN_WIDTH });
    const [atendentes, setAtendentes] = useState<any[]>([]);
    const [loadingAtendentes, setLoadingAtendentes] = useState(false);
    const [visible, setVisible] = useState(PAGE_SIZE);
    const listScrollRef = useRef(0);
    const listPaneRef = useRef<HTMLDivElement>(null);
    const isSideBySide = isTwoPane && !!id;

    useEffect(() => {
        const loadAtendentes = async () => {
            try {
                setLoadingAtendentes(true);
                const result = await atendentesService.getAll();
                setAtendentes(result?.data?.data || []);
            } catch (error) {
                console.error('Erro ao carregar atendentes:', error);
            } finally {
                setLoadingAtendentes(false);
            }
        };

        loadAtendentes();
    }, []);

    const reloadClients = () => {
        void loadClients(undefined, grupoId, atendenteId);
    };

    const filteredData = useMemo(() => {
        const searchTerm = normalizeSearch(search);
        const searchDigits = onlyDigits(search);

        return data
            .filter((row: DataRow) => {
                const matchesSelfScheduling =
                    !selfSchedulingStatus ||
                    (selfSchedulingStatus === 'blocked' && row.selfSchedulingBlocked) ||
                    (selfSchedulingStatus === 'available' && !row.selfSchedulingBlocked);
                const matchesSearch =
                    !searchTerm ||
                    normalizeSearch(row.displayName).includes(searchTerm) ||
                    normalizeSearch(row.email).includes(searchTerm) ||
                    (!!searchDigits && onlyDigits(row.phoneNumber).includes(searchDigits));

                return matchesSelfScheduling && matchesSearch;
            })
            .sort((a: DataRow, b: DataRow) => {
                // Nomes que nao comecam com letra formam o grupo `#`, sempre depois do Z.
                const hashOrder = Number(getInitial(a) === '#') - Number(getInitial(b) === '#');
                return (
                    hashOrder ||
                    getSortName(a).localeCompare(getSortName(b), 'pt-BR', {
                        sensitivity: 'base',
                    })
                );
            });
    }, [data, search, selfSchedulingStatus]);

    const sections = useMemo(() => {
        const groups: { letter: string; rows: DataRow[] }[] = [];

        filteredData.slice(0, visible).forEach((row: DataRow) => {
            const letter = getInitial(row);
            const current = groups[groups.length - 1];

            if (current?.letter === letter) {
                current.rows.push(row);
            } else {
                groups.push({ letter, rows: [row] });
            }
        });

        return groups;
    }, [filteredData, visible]);

    const selected: DataRow | undefined = data.find((row: DataRow) => row.uid === id);

    // carga inicial + recarga quando filtros server-side mudam
    useEffect(() => {
        reloadClients();
    }, [grupoId, atendenteId]);

    useEffect(() => {
        setVisible(PAGE_SIZE);
    }, [search, selfSchedulingStatus, atendenteId]);

    // Abrir um cliente leva o detalhe ao topo; ao fechar, a lista retoma a posicao em que estava.
    useEffect(() => {
        if (id) {
            window.scrollTo({ top: 0 });
        } else if (listScrollRef.current) {
            window.scrollTo({ top: listScrollRef.current });
            listScrollRef.current = 0;
        }
    }, [id]);

    // Lado a lado, a lista tem rolagem propria e precisa manter o cliente aberto visivel.
    useEffect(() => {
        const pane = listPaneRef.current;
        const row = pane?.querySelector<HTMLElement>('[aria-current="true"]');
        if (!isSideBySide || !pane || !row) return;

        const paneRect = pane.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();

        if (rowRect.top < paneRect.top || rowRect.bottom > paneRect.bottom) {
            pane.scrollTop += rowRect.top - paneRect.top - (pane.clientHeight - rowRect.height) / 2;
        }
    }, [id, isSideBySide]);

    const openClient = (row: DataRow) => {
        if (row.uid === id) {
            clear();
            return;
        }

        if (!id) listScrollRef.current = window.scrollY;
        setParams({ id: row.uid });
    };

    const hasFilters = !!search || !!selfSchedulingStatus || !!atendenteId;
    const showInitialLoading = loading && !data.length;

    const listPane = (
        <div ref={listPaneRef} className={`cli-list-pane ${!isTwoPane && id ? 'hidden' : ''}`}>
            <FilterClient
                search={search}
                setSearch={(value: string) => setFilters({ search: value })}
                atendenteId={atendenteId}
                setAtendenteId={(value: string) => setFilters({ atendenteId: value })}
                atendentes={atendentes}
                loadingAtendentes={loadingAtendentes}
                selfSchedulingStatus={selfSchedulingStatus}
                setSelfSchedulingStatus={(value: string) => setFilters({ selfSchedulingStatus: value })}
            />

            {showInitialLoading ? (
                <div className="space-y-2" aria-busy="true">
                    {[0, 1, 2, 3, 4, 5].map((item) => (
                        <div key={item} className="sf-skeleton h-14" />
                    ))}
                </div>
            ) : error && !data.length ? (
                <div className="sf-state">
                    <TriangleAlert aria-hidden />
                    <h2>Não foi possível carregar os clientes</h2>
                    <button type="button" className="btn btn-primary btn-sm" onClick={reloadClients}>
                        Tentar novamente
                    </button>
                </div>
            ) : filteredData.length === 0 ? (
                <div className="sf-state">
                    {hasFilters ? <Search aria-hidden /> : <Contact aria-hidden />}
                    <h2>{hasFilters ? 'Nenhum cliente encontrado' : 'Nenhum cliente ainda'}</h2>
                    {hasFilters ? (
                        <button
                            type="button"
                            className="sf-link"
                            onClick={() => setFilters({ search: '', atendenteId: '', selfSchedulingStatus: '' })}
                        >
                            Limpar filtros
                        </button>
                    ) : (
                        <p>Adicione clientes pelo botão + ou compartilhe sua agenda.</p>
                    )}
                </div>
            ) : (
                <>
                    <div className="cli-count">
                        {filteredData.length} {filteredData.length === 1 ? 'cliente' : 'clientes'}
                    </div>
                    <div className="cli-list-head" aria-hidden>
                        <span>Nome</span>
                        <span>Telefone</span>
                        <span>E-mail</span>
                        <span>Autoagendamento</span>
                    </div>
                    {sections.map(({ letter, rows }) => (
                        <section key={letter}>
                            <h2 className="cli-letter">{letter}</h2>
                            <ul className="sf-group">
                                {rows.map((row) => (
                                    <li key={row.uid}>
                                        <button
                                            type="button"
                                            className="cli-row"
                                            aria-current={row.uid === id ? 'true' : undefined}
                                            onClick={() => openClient(row)}
                                        >
                                            <AvatarUser
                                                photoURL={row.photoURL}
                                                name={row.displayName || 'Cliente'}
                                                size="h-9 w-9 !text-xs"
                                            />
                                            <span className="cli-row-body">
                                                <span className="cli-row-title">
                                                    {row.displayName || row.email || 'Sem nome'}
                                                </span>
                                                <span className="cli-row-sub">
                                                    {formatPhone(row.phoneNumber) || row.email || 'Sem contato'}
                                                </span>
                                            </span>
                                            {row.selfSchedulingBlocked && (
                                                <span className="cli-row-blocked" title="Autoagendamento bloqueado">
                                                    <Ban aria-hidden />
                                                    <span className="sr-only">Autoagendamento bloqueado</span>
                                                </span>
                                            )}
                                            {/* Colunas exibidas quando a lista tem largura para isso (Clientes.css). */}
                                            <span className="cli-col">{formatPhone(row.phoneNumber) || '—'}</span>
                                            <span className="cli-col" title={row.email || undefined}>
                                                {row.email || '—'}
                                            </span>
                                            <span
                                                className={`cli-col ${row.selfSchedulingBlocked ? 'is-blocked' : ''}`}
                                                title={getSelfSchedulingLabel(row)}
                                            >
                                                {getSelfSchedulingLabel(row)}
                                            </span>
                                            <ChevronRight className="cli-chevron" aria-hidden />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                    {filteredData.length > visible && (
                        <div className="mt-4 flex justify-center">
                            <button
                                type="button"
                                className="sf-link"
                                onClick={() => setVisible((current) => current + PAGE_SIZE)}
                            >
                                Mostrar mais ({filteredData.length - visible})
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    let detailPane = null;

    if (selected) {
        detailPane = (
            <ViewClient
                key={selected.uid}
                data={selected}
                atendentes={atendentes}
                isSideBySide={isSideBySide}
                onBack={clear}
                onClientUpdated={reloadClients}
                onClientRemoved={() => {
                    clear();
                    reloadClients();
                }}
            />
        );
    } else if (id && !showInitialLoading) {
        detailPane = (
            <div className="sf-state">
                <UserX aria-hidden />
                <h2>Cliente não encontrado</h2>
                <p>Ele pode ter sido removido ou não aparecer no filtro de atendente selecionado.</p>
                <button type="button" className="sf-link" onClick={clear}>
                    Voltar para clientes
                </button>
            </div>
        );
    }

    return (
        <div className={`cli-page px-2 ${isSideBySide ? 'is-two-pane' : ''}`}>
            {listPane}
            {detailPane}
        </div>
    );
}
